// App.tsx — Top-level layout for ConstructiVision Web PWA
// Renders:  WelcomeScreen (no project) OR TopBar + Sidebar + Viewer + PropsPanel
// Dialog overlay system driven by ui.activeDialog
// Toast driven by ui.toastMessage

import { useCallback, useEffect, useState } from 'react';
import { useStore } from './store/useStore';
import { useAuth } from './auth/AuthProvider';
import ReadOnlyBanner from './auth/ReadOnlyBanner';
import UpsellModal, { type UpsellReason } from './auth/UpsellModal';
import { registerUpsellHandler, unregisterUpsellHandler } from './utils/exportGateway';
import TopBar from './components/TopBar';
import Sidebar from './components/Sidebar';
import PropsPanel from './components/PropsPanel';
import ProjectView from './components/ProjectView';
import ElevationsView from './components/ElevationsView';
import PlansView from './components/PlansView';
import BracePlanView from './components/BracePlanView';
import CastLayoutView from './components/CastLayoutView';
import WelcomeScreen from './components/WelcomeScreen';
import DialogRouter from './components/DialogRouter';
import WarningAckDialog from './components/WarningAckDialog';
import PanelViewer3D from './components/viewer/PanelViewer3D';
import PanelCanvas2D from './components/viewer/PanelCanvas2D';
import ResizeHandle from './components/ResizeHandle';
import FeaturePlacementMenu from './components/FeaturePlacementMenu';
import EntityContextMenu from './components/EntityContextMenu';
import PwaUpdateBanner from './components/PwaUpdateBanner';
import PwaInstallButton from './components/PwaInstallButton';

const SIDEBAR_MIN = 120;
const SIDEBAR_MAX = 500;
const SIDEBAR_DEFAULT = Math.round(window.innerWidth * 0.2);
const PROPS_MIN = 160;
const PROPS_MAX = 600;
const PROPS_DEFAULT = Math.round(window.innerWidth * 0.2);

export default function App() {
  const project      = useStore(s => s.project);
  const ui           = useStore(s => s.ui);
  const { canEdit, session } = useAuth();
  const [upsell, setUpsell] = useState<UpsellReason | null>(null);

  // Wire the exportGateway → upsell modal handoff. The gateway calls this when
  // a /spend returns 402 (no tokens).
  useEffect(() => {
    registerUpsellHandler((reason) => setUpsell(reason));
    return () => unregisterUpsellHandler();
  }, []);

  // After a successful Stripe redirect, drop the ?checkout=success query and
  // let useAuth's window-focus refresh pick up the new balance.
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get('checkout') === 'success') {
      url.searchParams.delete('checkout');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  const commitPlacement   = useStore(s => s.commitPlacement);
  const cancelPlacement   = useStore(s => s.cancelPlacement);
  const closeContextMenu  = useStore(s => s.closeContextMenu);
  const copyEntity        = useStore(s => s.copyEntity);
  const pasteEntity       = useStore(s => s.pasteEntity);
  const editEntity        = useStore(s => s.editEntity);
  const deleteEntity      = useStore(s => s.deleteEntity);
  const toastMessage = ui.toastMessage;
  const activePanel  = project?.panels.find(p => p.id === ui.activePanelId) ?? null;
  const [sidebarW, setSidebarW] = useState(SIDEBAR_DEFAULT);
  const [propsW, setPropsW] = useState(PROPS_DEFAULT);

  const onSidebarResize = useCallback((dx: number) => {
    setSidebarW(w => Math.max(SIDEBAR_MIN, Math.min(SIDEBAR_MAX, w + dx)));
  }, []);

  const onPropsResize = useCallback((dx: number) => {
    // Dragging left = expand props, dragging right = shrink props
    setPropsW(w => Math.max(PROPS_MIN, Math.min(PROPS_MAX, w - dx)));
  }, []);

  if (!project) {
    return (
      <>
        <PwaUpdateBanner />
        <ReadOnlyBanner
          onSignIn={() => setUpsell('edit')}
          onUpgrade={() => (window.location.href = `${import.meta.env.BASE_URL}pricing`)}
        />
        <PwaInstallButton />
        <WelcomeScreen onSignInRequested={() => setUpsell('edit')} />
        {ui.activeDialog !== 'none' && <DialogRouter />}
        {ui.pendingWarnings && <WarningAckDialog />}
        {toastMessage && <div className="toast">{toastMessage}</div>}
        <UpsellModal
          open={upsell !== null}
          reason={upsell ?? 'edit'}
          onClose={() => setUpsell(null)}
          onSeeMembership={() => { window.location.href = `${import.meta.env.BASE_URL}pricing`; }}
          onSeeTokens={() => { window.location.href = `${import.meta.env.BASE_URL}pricing`; }}
        />
      </>
    );
  }

  const readOnly = !!session && !canEdit;

  return (
    <div className={`app-root${readOnly ? ' app-readonly' : ''}`}>
      <PwaUpdateBanner />
      <ReadOnlyBanner
        onSignIn={() => setUpsell('edit')}
        onUpgrade={() => (window.location.href = `${import.meta.env.BASE_URL}pricing`)}
      />
      <PwaInstallButton />
      <TopBar />

      {ui.editMode === 'project' && (
        <div className="app-layout">
          <ProjectView />
        </div>
      )}

      {ui.editMode === 'building' && (
        <div className="app-layout">
          <PlansView />
        </div>
      )}

      {ui.editMode === 'elevations' && (
        <div className="app-layout">
          <ElevationsView />
        </div>
      )}

      {ui.editMode === 'brace' && (
        <div className="app-layout">
          <BracePlanView />
        </div>
      )}

      {ui.editMode === 'cast' && (
        <div className="app-layout">
          <CastLayoutView />
        </div>
      )}

      {ui.editMode === 'panel' && (
        <div className="app-layout">
          <Sidebar style={ui.sidebarOpen ? { width: sidebarW, minWidth: sidebarW } : undefined} />
          {ui.sidebarOpen && <ResizeHandle onResize={onSidebarResize} />}

          <main className="viewport-area">
            {ui.viewMode === '2d' && activePanel
              ? <PanelCanvas2D panel={activePanel} />
              : <PanelViewer3D />
            }
          </main>

          {ui.propsOpen && <ResizeHandle onResize={onPropsResize} />}
          <PropsPanel style={ui.propsOpen ? { width: propsW, minWidth: propsW } : undefined} />
        </div>
      )}

      {/* Mode + view toggle moved into TopBar component */}

      {ui.activeDialog !== 'none' && <DialogRouter />}
      {ui.pendingWarnings && <WarningAckDialog />}
      {ui.placementContext && (
        <FeaturePlacementMenu
          screenX={ui.placementContext.screenX}
          screenY={ui.placementContext.screenY}
          onSelect={commitPlacement}
          onCancel={cancelPlacement}
        />
      )}
      {ui.contextMenu && (
        <EntityContextMenu
          screenX={ui.contextMenu.screenX}
          screenY={ui.contextMenu.screenY}
          entityId={ui.contextMenu.entityId}
          hasClipboard={ui.clipboard !== null}
          onEdit={(eid) => { closeContextMenu(); editEntity(eid); }}
          onCopy={(eid) => { closeContextMenu(); copyEntity(eid); }}
          onDelete={(eid) => { closeContextMenu(); deleteEntity(eid); }}
          onPaste={() => { closeContextMenu(); pasteEntity(ui.contextMenu!.panelX, ui.contextMenu!.panelY); }}
          onClose={closeContextMenu}
        />
      )}
      {toastMessage && <div className="toast">{toastMessage}</div>}
      <UpsellModal
        open={upsell !== null}
        reason={upsell ?? 'edit'}
        onClose={() => setUpsell(null)}
        onSeeMembership={() => { window.location.href = `${import.meta.env.BASE_URL}pricing`; }}
        onSeeTokens={() => { window.location.href = `${import.meta.env.BASE_URL}pricing`; }}
      />
    </div>
  );
}
